import { intlLocaleOf, type Locale } from "./locale";

/** Accepted date inputs: an ISO string, epoch milliseconds or a `Date`. */
export type DateInput = string | number | Date;

/** Default output: a calendar date (`12 Oct 2026` / `12 أكتوبر 2026`). */
export const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium" };

/** Preset for a date with a time of day. */
export const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "short",
};

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(intlLocaleOf(locale), options);
    formatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Formats a date in the app locale (not the browser's): Arabic keeps Latin digits (ADR-0006).
 * Returns `fallback` (default `"—"`) for a missing or unparsable value instead of "Invalid Date".
 */
export function formatDate(
  locale: Locale,
  value: DateInput | null | undefined,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_FORMAT,
  fallback = "—",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return formatterFor(locale, options).format(date);
}
